import {
  AggregationMethod,
  Column,
  CountAnalysis,
  Donut,
  DonutChartRequest,
  Filter,
  GroupedDonutRequest,
  GroupedDonutResponse,
  HistogramRequest,
} from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import { normalizeNullValuesForAGraph } from '../../utils/ApiUtils';
import { isAGraphEnabled } from './AGraphUtils';
import { awApiWithPkg } from './Base';
import { AladdinWealthServerGlobalParams } from './Constants';
import { AWSAnalysis } from './types';

export type AggregationAnalysis = AWSAnalysis<Column[]>;

export interface AssetAllocation {
  allocation: number;
  colTag: string;
}

export type BatchedDonutsAnalysis = AWSAnalysis<DonutChartRequest[]>;

// Partial because aggregateBy is optional
export type GroupedDonutsAnalysis = AWSAnalysis<Partial<GroupedDonutRequest>>;

// TODO remove GroupedDonutRequestV2 related types once @blk/awt-aladdinwealthserver fix the types
export interface GroupedDonutRequestV2 {
  aggregateBy: string;
  aggregationMethod: AggregationMethod;
  summarizeByList: string[];
}
export type GroupedDonutsAnalysisV2 = AWSAnalysis<Partial<GroupedDonutRequestV2>>;

export type GroupedDonutResponseV2 = { [colTag: string]: GroupedDonutResponse[] };

/**
 * This limit is documented in
 * {@link: https://webster.bfm.com/Wiki/display/AFWM/Produce+rule+category+based+status+columns+as+part+of+the+compl+column+-+AladdinWealthServer}
 */
export const GROUPED_DONUT_ANALYSIS_V2_SUMMARIZE_BY_LIST_LIMIT = 5;

export type HistogramAnalysisRequest = {
  aggregateTag?: string;
  analysisDate: string;
  benchType: string;
  pkg: string;
  key: string;
  filters: Filter[];
  restEndpoint: string;
  payload: {
    aggregator: HistogramRequest['aggregator'];
    binAttribute: HistogramRequest['binAttribute'];
    binSize: HistogramRequest['binSize'];
    end: HistogramRequest['end'];
    groupByAttribute?: HistogramRequest['groupByAttribute'];
    showOutliers?: Partial<HistogramRequest['showOutliers']>;
    start: HistogramRequest['start'];
  };
};

export type HistogramAnalysisDatum = {
  x: number;
  y: number;
  status?: string;
};

export type HistogramAnalysisResponse = {
  name: string;
  data: HistogramAnalysisDatum[];
}[];

export type SelectedTimePriodKey = 'numDays' | 'numMonths' | 'numQuarters' | 'numYears';

export type TimeSeriesRequest = {
  aggregateTag?: string;
  analysisDate: string;
  benchType: string;
  filters: Filter[];
  pkg: string | undefined;
  payload: {
    timeSeriesHistoricalDates: TimeSeriesPayloadHistoricalDates;
    timeSeriesRequests: TimeSeriesPayloadRequest[];
  };
};

export type EntityTimeSeriesRequest = {
  analysisDate: string;
  benchType: string;
  filters: Filter[];
  pkg: string | undefined;
  payload: {
    timeSeriesHistoricalDates: TimeSeriesPayloadHistoricalDates;
    timeSeriesRequests: TimeSeriesReferencePayloadRequest[];
  };
};

export type TimeSeriesPayloadHistoricalDates = {
  calendar: string;
  numDays: number;
  numMonths: number;
  numQuarters: number;
  numYears: number;
};

export type TimeSeriesPayloadRequest = {
  aggregationMethod: string;
  colTag: string;
  weightBy?: string;
};

export type TimeSeriesReferencePayloadRequest = {
  aggIDTag: string;
  aggIDValue: string;
  aggregationMethod: string;
  colTag: string;
  weightBy?: string;
};

export type TimeSeriesResponse = {
  timeSeriesResults: TimeSeriesResult[];
};

export type TimeSeriesResult = {
  colTag: string;
  dataPoints: TimeSeriesPoint[];
};

export type TimeSeriesPoint = { timestamp: number; date: string; value: null | number };

export function postBatchedDonutsAnalysis(
  request: BatchedDonutsAnalysis,
  cancelToken?: CancelToken
): Promise<AxiosResponse<Donut[]>> {
  const path = '/analysis/batch-donuts';
  return awApiWithPkg.post<Donut[]>(path, request, { cancelToken, params: { pkg: request.pkg } });
}

export function postGroupedDonutAnalysis(
  request: GroupedDonutsAnalysis,
  cancelToken?: CancelToken
): Promise<AxiosResponse<GroupedDonutResponse[]>> {
  const path = '/analysis/grouped-donut';
  const newRequest = isAGraphEnabled() ? normalizeNullValuesForAGraph(request, 'filters') : request;
  return awApiWithPkg.post<GroupedDonutResponse[]>(path, newRequest, { cancelToken, params: { pkg: request.pkg } });
}

export function postGroupedDonutAnalysisV2(
  request: GroupedDonutsAnalysisV2,
  cancelToken?: CancelToken
): Promise<AxiosResponse<GroupedDonutResponseV2>> {
  const path = isAGraphEnabled() ? '/v2/analysis/grouped-donut' : '/analysis/v2/grouped-donut';
  return awApiWithPkg.post<GroupedDonutResponseV2>(path, request, { cancelToken, params: { pkg: request.pkg } });
}

export function postCountAnalysis(
  request: CountAnalysis,
  params: AladdinWealthServerGlobalParams
): Promise<AxiosResponse<{ [key: string]: number }>> {
  const path = '/analysis/count';
  return awApiWithPkg.post<{ [key: string]: number }>(path, request, { params: { ...params, pkg: request.pkg } });
}

export function postAssetAllocationAnalysis(
  request: AggregationAnalysis,
  cancelToken?: CancelToken
): Promise<AxiosResponse<AssetAllocation[]>> {
  const path = 'analysis/asset-allocation';
  return awApiWithPkg.post<AssetAllocation[]>(path, request, { cancelToken, params: { pkg: request.pkg } });
}

export function postAggregation(request: AggregationAnalysis): Promise<AxiosResponse<number[]>> {
  const path = 'analysis/aggregation';
  return awApiWithPkg.post<number[]>(path, request, { params: { pkg: request.pkg } });
}

export function postHistogramAnalysis(
  request: HistogramAnalysisRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<HistogramAnalysisResponse>> {
  const path = '/analysis/histogram';
  return awApiWithPkg.post<HistogramAnalysisResponse>(path, request, { cancelToken, params: { pkg: request.pkg } });
}

export function postTimeSeriesAnalysis(
  request: TimeSeriesRequest,
  cancelToken?: CancelToken,
  pkg?: string
): Promise<AxiosResponse<TimeSeriesResponse>> {
  const path = '/analysis/analysis-time-series';
  return awApiWithPkg.post<TimeSeriesResponse>(path, request, { cancelToken, params: { pkg: request.pkg || pkg } });
}

export function postEntityTimeSeries(
  request: EntityTimeSeriesRequest,
  cancelToken?: CancelToken,
  pkg?: string
): Promise<AxiosResponse<TimeSeriesResponse>> {
  const path = '/analysis/entity-time-series';
  return awApiWithPkg.post<TimeSeriesResponse>(path, request, { cancelToken, params: { pkg: request.pkg || pkg } });
}
